import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  BellRing, ExternalLink, MessageCircle, Mail, Clock, RefreshCw,
  Home, Car, Shield, Scale, HeartPulse, Plane, Store,
  PiggyBank, TrendingUp, Building2, KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TYPE_ICONS = {
  casa: Home, auto: Car, rc: Shield, tutela_legale: Scale,
  malattia: HeartPulse, viaggi: Plane, negozio: Store,
  vita_3a: PiggyBank, vita_3b: TrendingUp,
  hypothek: Building2, mietkaution: KeyRound,
};

const KIND_BADGE_COLOR = {
  reminder_kvg_3m: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-300/30",
  reminder_vvg_30july: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-300/30",
  reminder_hypothek_6m: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-300/30",
  reminder_hypothek_3m: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-300/30",
  reminder_vita_3a_12m: "bg-prime/15 text-prime border-prime/30",
  reminder_vita_3b_12m: "bg-prime/15 text-prime border-prime/30",
  default: "bg-secondary text-foreground/80 border-border",
};

function daysUntil(iso) {
  if (!iso) return null;
  const due = new Date(iso);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((due - now) / 86400000);
}

function fmtDateCH(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function ReminderWidget({ days = 180 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/reminders/upcoming", { params: { days } });
        if (alive) setItems(r.data || []);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [days, reloadTick]);

  const runScan = async () => {
    if (scanning) return;
    setScanning(true);
    try {
      const r = await api.post("/reminders/scan", null, { params: { horizon_days: 365 } });
      toast.success(`${r.data.dispatched} neue Erinnerungen versendet`);
      setReloadTick(t => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan fehlgeschlagen");
    } finally {
      setScanning(false);
    }
  };

  if (loading) {
    return (
      <div className="prime-card p-6" data-testid="reminder-widget-loading">
        <div className="h-4 w-40 bg-secondary rounded animate-pulse mb-4" />
        <div className="space-y-2">
          <div className="h-12 bg-secondary/60 rounded animate-pulse" />
          <div className="h-12 bg-secondary/60 rounded animate-pulse" />
          <div className="h-12 bg-secondary/60 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="prime-card" data-testid="reminder-widget">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-prime/10 flex items-center justify-center">
            <BellRing className="w-4 h-4 text-prime" strokeWidth={1.8} />
          </div>
          <div>
            <div className="label-uppercase text-muted-foreground">Auto-Reminder</div>
            <h2 className="font-display font-bold text-xl tracking-tight">Anstehende Erinnerungen</h2>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              Fällig in den nächsten {days} Tagen · {items.length} Eintrag{items.length === 1 ? "" : "e"}
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={runScan}
          disabled={scanning}
          className="gap-1.5 text-xs"
          data-testid="reminder-scan-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${scanning ? "animate-spin" : ""}`} />
          {scanning ? "Scanne…" : "Jetzt scannen"}
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="p-12 text-center text-sm text-muted-foreground">
          Keine anstehenden Erinnerungen — alle Police laufen ausserhalb der Frist von {days} Tagen.
        </div>
      ) : (
        <div className="divide-y divide-border">
          {items.slice(0, 20).map((r, idx) => {
            const Icon = TYPE_ICONS[r.insurance_type] || BellRing;
            const due = daysUntil(r.due_date);
            const urgent = due !== null && due <= 14;
            const warning = due !== null && due > 14 && due <= 60;
            const badgeClass = KIND_BADGE_COLOR[r.kind] || KIND_BADGE_COLOR.default;
            const waText = encodeURIComponent(
              `Guten Tag ${r.client_name || ""}\n\nWir möchten Sie an Ihren Versicherungstermin erinnern:\n• ${r.company} — Police-Nr. ${r.policy_number}\n• ${r.anchor_label}: ${r.anchor_date}\n• Hinweis: ${r.label}\n\nFreundliche Grüsse\nWertia Finanz GmbH`
            );
            const digits = (r.client_phone || "").replace(/\D/g, "");
            const waLink = digits ? `https://wa.me/${digits.length === 10 && digits.startsWith("0") ? "41" + digits.slice(1) : digits}?text=${waText}` : null;
            return (
              <div
                key={`${r.policy_id}-${r.kind}-${idx}`}
                className="px-6 py-4 hover:bg-secondary/40 transition-colors group"
                data-testid={`reminder-row-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-md border flex items-center justify-center shrink-0 ${badgeClass}`}>
                    <Icon className="w-4 h-4" strokeWidth={1.8} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">
                        {r.company} <span className="text-muted-foreground">· {r.policy_number}</span>
                      </span>
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${badgeClass}`}>
                        {r.label}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                      <Link to={`/clients/${r.client_id}`} className="hover:text-prime hover:underline truncate font-medium">
                        {r.client_name || "—"}
                      </Link>
                      <span>·</span>
                      <span>{r.anchor_label}: <strong className="font-mono">{fmtDateCH(r.anchor_date)}</strong></span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-display font-extrabold text-2xl leading-none ${urgent ? "text-rose-600" : warning ? "text-amber-600" : "text-foreground"}`}>
                      {due !== null ? due : "—"}
                    </div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">
                      Tage
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2.5 pl-13" style={{ paddingLeft: "52px" }}>
                  <Link
                    to={`/clients/${r.client_id}`}
                    className="text-[10px] uppercase tracking-wider font-medium text-prime hover:underline inline-flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" /> Dossier öffnen
                  </Link>
                  {waLink && (
                    <a
                      href={waLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] uppercase tracking-wider font-medium text-emerald-600 hover:underline inline-flex items-center gap-1"
                      data-testid={`reminder-wa-${idx}`}
                    >
                      <MessageCircle className="w-3 h-3" /> WhatsApp Kunde
                    </a>
                  )}
                  <span className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground inline-flex items-center gap-1">
                    <Mail className="w-3 h-3" /> E-Mail in Warteschlange
                  </span>
                  <span className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Fällig {fmtDateCH(r.due_date)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
